import React from "react"

import { Card } from "@/components/ui/card"
import { Combobox } from "@/components/ui/combobox"
import Tiptap from "../../../tiptap/tiptap";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useOrder } from "../../../contexts/OrderContext";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import cournoyer from "@/cournoyer";
import { BookOpenIcon, CrossIcon, FileIcon, MessageCircleQuestionIcon, MessageSquare, XIcon } from "lucide-react";
import { TextShimmer } from "@/components/ui/text-shimmer";
import { AutoResizingTextarea } from "../mandate/MandateForm";
import { useDocuments } from "@/contexts/DocumentsContext";
import { Button } from "@/components/ui/button";

export default function ReportForm() {
    const { order } = useOrder()
    const { sectionsContainer } = useDocuments()

    const sections = cournoyer
    
    const isEmpty = Object.keys(order.sections).length <= 1

    return <div className="flex flex-grow flex-col gap-2 min-h-0 min-w-0">
        <Card className="flex flex-grow overflow-hidden relative">
            <div className="flex flex-grow flex-col p-6 overflow-y-auto gap-2 h-full" style={{
                position: 'absolute',
                top: '0px',
                height: '100%',
                width: '100%',
            }} ref={sectionsContainer}>
                {isEmpty && <div className="flex flex-grow justify-center items-center h-full">
                    <div className="select-none flex flex-col items-center gap-4 text-muted-foreground transform translate-y-[-50%] opacity-50">
                        <FileIcon className="h-16 w-16" strokeWidth={1} />
                        Page Blanche
                    </div>
                </div>}

                {!isEmpty && sections.map((section, i) => {
                    return <Section key={i} isMain section={section} />
                })}
            </div>
        </Card>
    </div>
}

export function Section({ section, isMain }) {
    const { order, setOrder } = useOrder()

    const value = order.sections[section.value]

    if (!(section.value in order.sections) && (!section.sections || !section.sections.map(s => s.value).some(s => Object.keys(order.sections).includes(s)))) {
        return null
    }

    const update = (newValue) => {
        let newOrder = {
            ...order,
            is_sections_dirty: true,
            sections: {
                ...order.sections,
                [section.value]: newValue
            }
        }

        if (!order.sections_comment[section.value]) {
            newOrder.sections_comment = {
                ...order.sections_comment,
                [section.value]: ''
            }
        }

        setOrder(newOrder)
    }

    const cancelGeneration = () => {
        setOrder(prev => {
            const updated = { ...prev.sections }
            delete updated[section.value]
            return { ...prev, sections: updated }
        })
    }

    const isGenerating = value === 'LOADING' || section.sections?.some(s => order.sections[s.value] === 'LOADING')

    return <div className="flex flex-col">
        {!section.hideHeader && <div className={cn("font-semibold select-none px-[32px] font-tt-norms", !isMain && "px-[42px]", isMain && "heading mt-2", ['antecedents'].includes(section.value) && "pt-4")} id={`heading-${section.value}`} data-report={section.value} data-transcription={section.transcription_key}>
            <div className={"border-primary flex items-center relative"} style={isMain && { padding: "2px 8px", borderWidth: '3px', borderStyle: 'solid' }}>
                {isGenerating && section.label && <TextShimmer>{typeof section.label === 'function' ? section.labelTranscription : section.label}</TextShimmer>}
                {!isGenerating && (typeof section.label === 'function' ? section.label(order) : section.label)}

                {isGenerating && <Button onClick={cancelGeneration} className="ml-auto absolute right-0" size={"icon"} variant={"ghost"}><XIcon /></Button>}
            </div>
        </div>}

        {section.sections && <div className="flex flex-col gap-2">
            {section.sections.map((subsection, i) => <Section key={i} section={subsection} />)}
        </div>}

        {!isGenerating && !section.sections && <>
            {!!section.Component && <section.Component />}
            {!section.Component && <Tiptap readOnly={!!order.finished_at} enableBold={section.value !== 'conclusion_static'} contentKey={section.value} onContentChange={update} />}

            {typeof order.sections_comment[section.value] !== 'undefined' && <div className="px-[42px] relative">
                <MessageSquare className="w-6 h-6 absolute" strokeWidth={1} style={{ top: 20, left: -0 }} />
                <AutoResizingTextarea className="mt-2" placeholder="Quel élément a-t-il manqué à la génération ?" value={order.sections_comment[section.value]} onChange={(e) => setOrder({
                    ...order,
                    sections_comment: {
                        ...order.sections_comment,
                        [section.value]: e.target.value
                    }
                })} />
            </div>}
        </>}
    </div>
}

export function PatientDocument({ readOnly }) {
    const { order, setOrder } = useOrder()
    const documents_count = React.useMemo(() => order.sections.documents_count > 1 ? 2 : order.sections.documents_count, [order.sections.documents_count])

    const update = (value) => {
        setOrder({
            ...order,
            sections: {
                ...order.sections,
                ['documentProvided']: value
            }
        })
    }

    return <div className="flex flex-col">
        <div className="px-[32px] pt-2">
            <Select disabled={!!order.finished_at} value={documents_count} onValueChange={(value) => setOrder({
                ...order,
                sections: {
                    ...order.sections,
                    documents_count: value
                }
            })}>
                <SelectTrigger>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectItem value={0}>Aucun document</SelectItem>
                        <SelectItem value={1}>Un document</SelectItem>
                        <SelectItem value={2}>Plusieurs documents</SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>

        {documents_count > 0 && <Tiptap readOnly={!!order.finished_at} content={order.sections['documentProvided']} onContentChange={update} />}
    </div>
}